import { Fragment, useEffect, useRef } from 'react'
import classNames from 'classnames'
import { useShallow } from 'zustand/react/shallow'
import RoleCard from 'components/RoleCard'
import useRelationsContent from 'hooks/api/useRelationsContent'
import useContent from 'hooks/api/useContent'
import { t } from 'helpers'
import { useAppStore } from 'store'
import { RELATIONS } from 'types/enums'
import { Content, Relation } from 'types/app'
import s from './index.module.scss'
import CreateCardPlaceholder from 'components/CreateCardPlaceholder'

export interface RelationListItemProps {
	actions?: {
		label: string
		icon: JSX.Element
		onClick: (
			content: Content,
			userId?: string,
			prodId?: string,
			relation?: Relation,
		) => void
	}[]
	showUser?: boolean
	showInfo?: boolean
	cardType?: string
}

interface Props extends RelationListItemProps {
	relations: Relation[]
	place?: 'sidebar' | 'default'
	onCountUpdate?: (count: number) => void
	InvitedCount?: number
	RelationName?: string
}

const RelationContentList = ({
	relations,
	actions,
	showUser = true,
	showInfo = true,
	place = 'default',
	cardType,
	onCountUpdate,
	InvitedCount,
	RelationName
}: Props): JSX.Element => {
	const [search] = useAppStore(
		useShallow((state) => [state.localSearchSidebar?.search || '']),
	)

	const successCountRef = useRef(0)
	const attemptedRef = useRef(0)
	const failedRelationsRef = useRef<{ sourceId: string }[]>([])
	const hasReported = useRef(false)
	useEffect(() => {
		successCountRef.current = 0
		attemptedRef.current = 0
		failedRelationsRef.current = []
	}, [relations])

	const handleLoad = (success: boolean) => {
		attemptedRef.current++
		if (success) {
			successCountRef.current++
		}
		if (attemptedRef.current === relations.length) {
			onCountUpdate?.(successCountRef.current)
		}
	}

	const handleError = (fail: { sourceId: string }) => {
		failedRelationsRef.current.push(fail)
		handleLoad(false)
	}
	useEffect(() => {
		if (hasReported.current) return
		if (attemptedRef.current < relations.length) {
			onCountUpdate?.(successCountRef.current)
			hasReported.current = true
		}
	}, [relations, onCountUpdate])

	return (
		<div className={classNames(s.container, s[place])}>
			{relations?.map((relation) => (
				<RelationList
					key={relation.sourceId + relation.targetId}
					cardType={cardType}
					relation={relation}
					showUser={showUser}
					showInfo={showInfo}
					actions={actions}
					search={search}
					onLoad={handleLoad}
					onError={handleError}
					InvitedCount={InvitedCount}
					RelationName={RelationName}
				/>
			))}
		</div>
	)
}

export default RelationContentList

const RelationList = ({
	relation,
	onLoad,
	onError,
	InvitedCount,
	RelationName,
	...props
}: RelationListItemProps & {
	relation: Relation
	search: string
	onLoad?: (success: boolean) => void
	onError?: (failure: { sourceId: string }) => void
	InvitedCount?: number
	RelationName?: string
}): JSX.Element => {
	const { content, isLoading } = useContent(relation.sourceId, 'full')
	const hasReported = useRef(false)

	useEffect(() => {
		if (hasReported.current || isLoading) return
		const title = content?.['Globalizationen-USContentTitle']?.toLowerCase() || ''
		const match = title.includes(props.search.toLowerCase())
		if (content) {
			onLoad?.(true)
			hasReported.current = true
		} else {
			onError?.({ sourceId: relation.sourceId })
			hasReported.current = true
		}
	}, [content, props.search, onLoad, onError, relation.sourceId])

	if (RelationName && RelationName == 'Invitation') {
		return (
			<>
				{InvitedCount && InvitedCount >= 1 ?
					content &&
					content?.['Globalizationen-USContentTitle']
						?.toLowerCase()
						?.includes(props.search.toLowerCase()) && (
						<RelationListItem content={content} relation={relation} {...props} />
					) : <div>
					<CreateCardPlaceholder
						label="APP_RESULT_NOT_FOUND"
						style={{ height: '165px' }}
					/>
				</div>}
			</>)
	} else {
		return (content &&
			content?.['Globalizationen-USContentTitle']
				?.toLowerCase()
				?.includes(props.search) ? (
			<RelationListItem content={content} relation={relation} {...props} />
		) : (
			<></>
		)
		)
	}

}

export const RelationListItem = ({
	content,
	actions,
	relation,
	showUser,
	showInfo,
	cardType,
}: RelationListItemProps & {
	content: Content
	relation: Relation
}): JSX.Element => {
	const [projectId] = useAppStore(
		useShallow((state) => [state.localSearchSidebar?.projectId]),
	)
	const { data } = useRelationsContent(
		[RELATIONS.PLAYLIST],
		content?.ContentId,
		'Content',
		'/Lookup',
		true,
	)

	const prod = data?.[RELATIONS.PLAYLIST]?.[0]

	const relatedUserId = showUser ? relation?.targetId : undefined

	return !projectId || projectId === prod?.ContentId ? (
		<RoleCard
			key={content.ContentId}
			content={content}
			showInfo={showInfo}
			relatedUserId={relatedUserId}
			type={cardType ? (cardType as 'casting') : undefined}
			relation={relation}
			children={
				<>
					{actions?.map((action) => (
						<Fragment key={action?.label}>
							<div
								className="hoverButtonWrapper"
								onClick={() =>
									action.onClick(
										content,
										relatedUserId,
										prod?.ContentId,
										relation,
									)
								}>
								{action.icon}
								<span>{t(action.label)}</span>
							</div>
						</Fragment>
					))}
				</>
			}
		/>
	) : (
		<></>
	)
}
